<template>
    <BaseDialog v-model:open="open" :title="t('collaborationProfile.sources.title')" title-style="serif">
        <p class="body text-ink-muted">{{ t("collaborationProfile.sources.description") }}</p>
        <FreeTextUpload v-model:free-text="freeText" :free-text-enabled="freeTextEnabled" />

        <template v-if="documentUploadEnabled">
            <ProfileDocumentsHeading />
            <ProfileFileUploadArea :accepted-file-types="acceptedFileTypes" :is-uploading="isUploading" @file-upload="handleFileUpload" />
            <div class="flex flex-col max-h-48 overflow-y-auto gap-2">
                <FileAttachedTag
                    v-for="file in uploadedFiles"
                    :key="file.name"
                    :file="file"
                    is-removable
                    data-testid="profile-source-file-attached-tag"
                    @remove="removeFile(file.id)" />
            </div>
            <BaseNotification v-if="fileUploadError" theme="error" :text="fileUploadError" @dismiss="fileUploadError = ''" />
        </template>

        <template #footer>
            <BaseButton theme="tertiary" :disabled="isUploading" @click="open = false">{{ t("common.cancel") }}</BaseButton>
            <BaseTooltip side="top" :disabled="hasSource">
                <template #trigger>
                    <!-- A disabled button emits no pointer events, so the wrapper carries the hover that opens the tooltip. -->
                    <span>
                        <BaseButton theme="primary" :disabled="isUploading || !hasSource" data-testid="profile-create-from-sources" @click="handleSave">
                            {{ t("components.profileWizard.upload.createProfile") }}
                        </BaseButton>
                    </span>
                </template>
                <template #content>
                    <span class="body-small">{{
                        documentUploadEnabled ? t("components.profileWizard.upload.hintWithDocuments") : t("components.profileWizard.upload.hintTextOnly")
                    }}</span>
                </template>
            </BaseTooltip>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import FreeTextUpload from "~vue/CollaborationProfile/FreeTextUpload.vue";
import ProfileDocumentsHeading from "~vue/CollaborationProfile/ProfileDocumentsHeading.vue";
import ProfileFileUploadArea from "~vue/CollaborationProfile/ProfileFileUploadArea.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { useProfileSources } from "~vue/composables/useProfileSources";
import { i18n } from "~vue/i18n";
import { computed, ref } from "vue";

import FileAttachedTag from "../FileAttachedTag.vue";

const { t } = i18n.global;

const { documentUploadEnabled = true, freeTextEnabled = false } = defineProps<{ documentUploadEnabled?: boolean; freeTextEnabled?: boolean }>();

const emit = defineEmits<{ createProfile: [fileIds: number[], freeText: string] }>();

const open = defineModel<boolean>("open", { default: false });

const { uploadedFiles, acceptedFileTypes, handleFileUpload, isUploading, fileUploadError, removeFile } = useProfileSources();

const freeText = ref<string>("");

const hasSource = computed(() => !freeTextEnabled || !!freeText.value.trim() || uploadedFiles.value.length > 0);

const handleSave = () => {
    emit(
        "createProfile",
        uploadedFiles.value.map((file) => file.id),
        freeText.value.trim(),
    );
    open.value = false;
};
</script>
